/*
 * DD AdminUI
 * Copyright (C) Fraunhofer IESE 2019 - 2025 Stefan Schweitzer, Shyalan Ramesh, Rafael Aranda López King,
 * Florian Wege, Johannes Schneider, Kazi Rezoanur Rahman, Christoph Müller, Pedram Jokar,
 * Mher Ter-Tovmasyan, Ben Burkhard, Matthias Gerbershagen, Anna Kleiner, Muhammad Tariq,
 * Adeline Silva Schäfer
 *
 * SPDX-License-Identifier: Apache-2.0
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import React from 'react';
import { InputAdornment, Link } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { ReadOnlyTextFieldView } from '../ReadOnlyTextField.view';

const useStyles = makeStyles({
	emailLinkContainer: {
		width: 'inherit',
		fontWeight: 'bold'
	},
	input: {
		width: '0' // Since we are not using the input but label to show the email, we do not need the input space.
	}
});

interface Props {
	email?: string;
	emailVerified?: boolean;
}

const EmailTextFieldView = (props: Props): JSX.Element => {
	const { email, emailVerified } = props;
	const classes = useStyles();

	return (
		<ReadOnlyTextFieldView
			id="email"
			name="email"
			label="E-Mail-Adresse"
			type="email"
			InputProps={{
				startAdornment: (
					<InputAdornment position="start" className={classes.emailLinkContainer}>
						<Link href={`mailto:${email ?? ''}`}>{email ?? ''}</Link>
					</InputAdornment>
				),
				endAdornment: (
					<InputAdornment position="end">
						{emailVerified ? <CheckCircleOutlineIcon color="primary" /> : ''}
					</InputAdornment>
				),
				readOnly: true,
				classes: { input: classes.input }
			}}
			InputLabelProps={{
				shrink: true
			}}
			fullWidth
		/>
	);
};

export default EmailTextFieldView;
